import React, { useEffect, useState, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import { closeModal } from "../../../redux/Modals/ModalActions";
import useTheme from "../../../hooks/useTheme";
import { AppRow } from "../Layouts";
import { AppLinkedText, CloseCircleButton } from "../CustomButtons";
import { SizedBox } from "../Sizes";
import { useResponsive } from "../ResponsiveContext";
import { t } from "i18next";

const ModalBottomSheet = ({
    closeModal,
    children,
    canScroll = true,
    height: initialHeight = 70,
    minHeight = 20,
}) => {
    const theme = useTheme();
    const { isMobile } = useResponsive();
    const location = useLocation();

    // Refs
    const sheetRef = useRef(null);
    const contentRef = useRef(null);
    const overlayRef = useRef(null);
    const initialPathRef = useRef(location.pathname);

    // State
    const [isVisible, setIsVisible] = useState(false);
    const [translateY, setTranslateY] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [startY, setStartY] = useState(0);
    const [lastY, setLastY] = useState(0);
    const [velocity, setVelocity] = useState(0);
    const [lastTime, setLastTime] = useState(0);

    // Animation frame ref
    const animationRef = useRef(null);

    // Close modal on route change
    useEffect(() => {
        if (location.pathname !== initialPathRef.current) {
            handleClose();
        }
    }, [location]);

    // Mount animation and body scroll lock
    useEffect(() => {
        document.body.style.overflow = 'hidden';

        requestAnimationFrame(() => {
            setIsVisible(true);
        });

        return () => {
            document.body.style.overflow = 'unset';
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, []);

    // Smooth position update
    const updatePosition = useCallback((newTranslateY, newOpacity = null) => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }

        animationRef.current = requestAnimationFrame(() => {
            if (sheetRef.current) {
                sheetRef.current.style.transform = `translateX(-50%) translateY(${newTranslateY}px)`;
            }
            if (overlayRef.current && newOpacity !== null) {
                overlayRef.current.style.opacity = newOpacity;
            }
        });
    }, []);

    // Smooth close animation
    const handleClose = useCallback(() => {
        if (sheetRef.current) {
            sheetRef.current.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        }
        if (overlayRef.current) {
            overlayRef.current.style.transition = 'opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        }

        // Animate sheet off-screen and fade overlay
        updatePosition(window.innerHeight, 0);
        setTimeout(() => {
            setIsVisible(false);
            closeModal();
        }, 400); // Match transition duration
    }, [closeModal, updatePosition]);

    // Touch handlers for drag handle
    const handleTouchStart = useCallback((e) => {
        const touch = e.touches[0];
        setStartY(touch.clientY);
        setLastY(touch.clientY);
        setIsDragging(true);
        setVelocity(0);
        setLastTime(Date.now());

        if (sheetRef.current) {
            sheetRef.current.style.transition = 'none';
        }
        if (overlayRef.current) {
            overlayRef.current.style.transition = 'none';
        }
    }, []);

    const handleTouchMove = useCallback((e) => {
        if (!isDragging) return;

        const touch = e.touches[0];
        const currentY = touch.clientY;
        const deltaY = currentY - startY;
        const currentTime = Date.now();

        const timeDelta = currentTime - lastTime;
        if (timeDelta > 0) {
            setVelocity((currentY - lastY) / timeDelta);
        }

        setLastY(currentY);
        setLastTime(currentTime);

        if (deltaY > 0) {
            const maxDrag = window.innerHeight * 0.5;
            const dampedDelta = deltaY > maxDrag ? maxDrag + (deltaY - maxDrag) * 0.2 : deltaY;

            setTranslateY(dampedDelta);
            const progress = Math.min(dampedDelta / (window.innerHeight * 0.3), 1);
            const opacity = 1 - progress * 0.7;

            updatePosition(dampedDelta, opacity);
        }
    }, [isDragging, startY, lastY, lastTime, updatePosition]);

    const handleTouchEnd = useCallback(() => {
        if (!isDragging) return;

        setIsDragging(false);

        if (sheetRef.current) {
            sheetRef.current.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        }
        if (overlayRef.current) {
            overlayRef.current.style.transition = 'opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        }

        const threshold = window.innerHeight * 0.15;
        if (translateY > threshold || velocity > 0.5) {
            handleClose();
        } else {
            setTranslateY(0);
            updatePosition(0, 1);
        }
    }, [isDragging, translateY, velocity, updatePosition, handleClose]);

    // Mouse handlers for drag handle
    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        setStartY(e.clientY);
        setLastY(e.clientY);
        setIsDragging(true);
        setVelocity(0);
        setLastTime(Date.now());

        if (sheetRef.current) {
            sheetRef.current.style.transition = 'none';
        }
        if (overlayRef.current) {
            overlayRef.current.style.transition = 'none';
        }
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!isDragging) return;

        const currentY = e.clientY;
        const deltaY = currentY - startY;
        const currentTime = Date.now();

        const timeDelta = currentTime - lastTime;
        if (timeDelta > 0) {
            setVelocity((currentY - lastY) / timeDelta);
        }

        setLastY(currentY);
        setLastTime(currentTime);

        if (deltaY > 0) {
            const maxDrag = window.innerHeight * 0.5;
            const dampedDelta = deltaY > maxDrag ? maxDrag + (deltaY - maxDrag) * 0.2 : deltaY;

            setTranslateY(dampedDelta);
            const progress = Math.min(dampedDelta / (window.innerHeight * 0.3), 1);
            const opacity = 1 - progress * 0.7;

            updatePosition(dampedDelta, opacity);
        }
    }, [isDragging, startY, lastY, lastTime, updatePosition]);

    const handleMouseUp = useCallback(() => {
        handleTouchEnd();
    }, [handleTouchEnd]);

    // Scroll handler for mouse wheel
    const handleScroll = useCallback((e) => {
        if (!contentRef.current || isDragging) return;

        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        const isAtTop = scrollTop === 0;
        const isScrollingUp = e.deltaY < 0;
        const hasScrollableContent = scrollHeight > clientHeight;

        if (isAtTop && isScrollingUp && hasScrollableContent && Math.abs(e.deltaY) > 10) {
            e.preventDefault();
            handleClose();
        }
    }, [isDragging, handleClose]);

    // Content touch handlers
    const handleContentTouchStart = useCallback((e) => {
        if (!contentRef.current) return;

        const touch = e.touches[0];
        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;

        contentRef.current.touchState = {
            startY: touch.clientY,
            startScrollTop: scrollTop,
            isAtTop: scrollTop === 0,
            lastY: touch.clientY,
            startTime: Date.now(),
            hasScrollableContent: scrollHeight > clientHeight
        };
    }, []);

    const handleContentTouchMove = useCallback((e) => {
        if (!contentRef.current || isDragging) return;

        const touchState = contentRef.current.touchState;
        if (!touchState) return;

        const touch = e.touches[0];
        const deltaY = touch.clientY - touchState.startY;
        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        const isCurrentlyAtTop = scrollTop === 0;
        const currentTime = Date.now();

        // Calculate velocity for scroll direction
        const timeDelta = currentTime - touchState.startTime;
        const touchVelocity = timeDelta > 0 ? (touch.clientY - touchState.lastY) / timeDelta : 0;

        touchState.lastY = touch.clientY;

        // Handle scroll-to-close (fast upward flick at top)
        if (touchState.isAtTop && isCurrentlyAtTop && deltaY < -30 && touchState.hasScrollableContent && touchVelocity < -0.8) {
            if (e.cancelable) {
                e.preventDefault();
            }
            handleClose();
            delete contentRef.current.touchState;
            return;
        }

        // Handle drag-to-close (downward drag at top)
        if (touchState.isAtTop && isCurrentlyAtTop && deltaY > 15 && touchState.hasScrollableContent) {
            if (e.cancelable) {
                e.preventDefault();
            }

            setStartY(touchState.startY);
            setLastY(touch.clientY);
            setIsDragging(true);
            setVelocity(0);
            setLastTime(Date.now());

            if (sheetRef.current) {
                sheetRef.current.style.transition = 'none';
            }
            if (overlayRef.current) {
                overlayRef.current.style.transition = 'none';
            }

            const maxDrag = window.innerHeight * 0.5;
            const adjustedDelta = deltaY - 15;
            const dampedDelta = adjustedDelta > maxDrag ? maxDrag + (adjustedDelta - maxDrag) * 0.2 : adjustedDelta;

            setTranslateY(dampedDelta);
            const progress = Math.min(dampedDelta / (window.innerHeight * 0.3), 1);
            const opacity = 1 - progress * 0.7;

            updatePosition(dampedDelta, opacity);
            delete contentRef.current.touchState;
        }
    }, [isDragging, updatePosition, handleClose]);

    const handleContentTouchEnd = useCallback(() => {
        if (contentRef.current) {
            delete contentRef.current.touchState;
        }
        if (isDragging) {
            handleTouchEnd();
        }
    }, [isDragging, handleTouchEnd]);

    // Non-passive touch event listeners
    useEffect(() => {
        const contentElement = contentRef.current;
        if (!contentElement) return;

        contentElement.addEventListener('touchmove', handleContentTouchMove, { passive: false });

        return () => {
            contentElement.removeEventListener('touchmove', handleContentTouchMove);
        };
    }, [handleContentTouchMove]);

    // Global mouse listeners
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    return (
        <>
            <div
                ref={overlayRef}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    zIndex: 1000,
                    opacity: isVisible ? 1 : 0,
                    transition: isVisible ? 'opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none',
                    pointerEvents: isVisible ? 'auto' : 'none',
                }}
                onClick={handleClose}
            />

            <div
                ref={sheetRef}
                style={{
                    position: 'fixed',
                    bottom: 0,
                    left: '50%',
                    transform: `translateX(-50%) translateY(${isVisible ? '0px' : '100%'})`,
                    width: '100%',
                    maxWidth: '100%',
                    height: `${initialHeight}vh`,
                    minHeight: `${minHeight}vh`,
                    backgroundColor: 'white',
                    borderRadius: '1rem 1rem 0 0',
                    zIndex: 1001,
                    transition: isVisible ? 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none',
                    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        paddingTop: theme.spacing.elementPadding,
                        paddingLeft: theme.spacing.contentPadding,
                        paddingRight: theme.spacing.contentPadding,
                        paddingBottom: theme.spacing.elementPadding,
                        cursor: 'grab',
                        touchAction: 'none',
                        position: 'relative'
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onMouseDown={handleMouseDown}
                >
                    {/* Close button positioned absolutely in top right for mobile */}
                    {isMobile && (
                        <div style={{
                            position: 'absolute',
                            top: theme.spacing.elementPadding,
                            right: theme.spacing.contentPadding,
                            zIndex: 10
                        }}>
                            <AppLinkedText
                                text={t('close')}
                                small={isMobile}
                                onClick={handleClose}
                                underline={true}
                            />
                        </div>
                    )}

                    {/* Desktop layout with space-between */}
                    {!isMobile ? (
                        <AppRow mainAxisAlignment={'space-between'} crossAxisAlignment={'center'} style={{ width: '100%' }}>
                            <SizedBox width={'1.8rem'} />

                            <div
                                style={{
                                    width: '2.5rem',
                                    height: '4px',
                                    backgroundColor: '#E0E1E2',
                                    borderRadius: '6px',
                                }}
                            />

                            <AppLinkedText
                                text={t('close')}
                                small={isMobile}
                                onClick={handleClose}
                                underline={true}
                            />
                        </AppRow>
                    ) : (
                        /* Mobile layout - only drag handle centered */
                        <div
                            style={{
                                width: '2.5rem',
                                height: '4px',
                                backgroundColor: '#E0E1E2',
                                borderRadius: '6px',
                            }}
                        />
                    )}
                </div>

                <div
                    ref={contentRef}
                    className="modal-bottom-sheet-content"
                    style={{
                        flex: 1,
                        overflowY: canScroll ? 'auto' : 'hidden',
                        WebkitOverflowScrolling: 'touch',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        justifyContent: 'flex-start',
                        alignContent: 'flex-start',
                        textAlign: 'left',
                    }}
                    onWheel={handleScroll}
                    onTouchStart={handleContentTouchStart}
                    onTouchMove={handleContentTouchMove}
                    onTouchEnd={handleContentTouchEnd}
                >
                    {children}
                </div>
            </div>

            <style>
                {`
                    .modal-bottom-sheet-content::-webkit-scrollbar {
                        display: none !important;
                    }
                    
                    .modal-bottom-sheet-content {
                        -webkit-overflow-scrolling: touch;
                        scroll-behavior: smooth;
                    }
                    
                    .modal-bottom-sheet-content > * {
                        flex: 0 1 auto;
                        width: 100%;
                        box-sizing: border-box;
                    }
                    
                    .modal-bottom-sheet-dragging * {
                        user-select: none !important;
                        -webkit-user-select: none !important;
                        -moz-user-select: none !important;
                        -ms-user-select: none !important;
                    }
                `}
            </style>
        </>
    );
};

export default connect(null, { closeModal })(ModalBottomSheet);
export const MODAL_BOTTOM_SHEET = "ModalBottomSheet";